<template>
  <div class="f-v f-ast">
    <donut-modal-sections-title :title="modalState.title" />
    <donut-loading-dots v-if="modalState.loading" />
    <template v-else>
      <div class="t">{{ modalState.primaryCopy }}</div>
      <div v-if="modalState.secondaryCopy" class="t-sm gray ital m-tx1">{{ modalState.secondaryCopy }}</div>
    </template>
    <!-- Simulated donut-modal-sections-actions -->
    <div class="f-ac f-je m-tx3">
      <div
        v-if="modalState.secondaryFunction"
        :class="`t link c-p ${modalState.primaryFunction ? 'm-r' : ''}`"
        @click="modalState.secondaryFunction"
      >
        {{ modalState.secondaryFunctionText }}
      </div>
      <button
        v-if="modalState.primaryFunction"
        :class="{ disabled: modalState.primaryFunctionDisabled }"
        @click="modalState.primaryFunction"
      >
        {{ modalState.primaryFunctionText }}
      </button>
    </div>
  </div>
</template>

<script setup>
  import Network from 'lib/api/network';
  import { ref, computed, onMounted } from 'vue';
  import { useStore } from 'vuex';

  const { state, commit, dispatch } = useStore();

  const apply = (rescheduleForCohort) => {
    dispatch('onboardingSessions/rescheduleEvent', { ...state.modal.data, rescheduleForCohort });
    // We want to call closeModal second or else it will have wiped the data, but note that the async nature of dispatch
    // is such that it will close instantly. This is desired behavior, as the event card already moved in the FE
    // in anticipation of the rescheduleEvent API call being successful
    commit('closeModal');
  };

  const { batchOnly } = state.modal.data;
  const startDate = state.onboardingSessions.session.batch.start_date;

  const fetchingBatchCounterparts = ref(true);
  const batchCounterparts = ref([]);
  const modalState = computed(() => {
    // Establish base status as the loading state. Unless fetchingBatchCounterparts is false, just return this status.
    const status = {
      loading: true,
      title: 'Apply to full cohort?',
      primaryCopy: null,
      secondaryCopy: null,
      primaryFunction: () => { apply(true); },
      primaryFunctionText: 'Apply to cohort',
      primaryFunctionDisabled: true,
      secondaryFunction: null,
      secondaryFunctionText: null,
    };
    if (!fetchingBatchCounterparts.value) {
      status.loading = false;
      if (batchCounterparts.value) {
        // Presence of batchCounterparts means a successful fetch. Parse the information.
        const occurred = batchCounterparts.value.filter(e => e.occurred).length;
        const occurredCopy = `${occurred} ${occurred === 1 ? 'person' : 'people'}`;
        const movingToPast = batchCounterparts.value.filter(e => e.moving_to_past).length;
        const movingToPastCopy = `${movingToPast} ${movingToPast === 1 ? 'person' : 'people'}`;

        if (batchOnly) {
          status.secondaryFunction = () => { dispatch('onboardingSessions/cancelEventReschedule', state.modal.data); };
          status.secondaryFunctionText = 'Close';
          if (movingToPast) {
            status.primaryCopy = `This message goes out once to everyone in the ${startDate} cohort, but the new time is already in the past for ${movingToPastCopy}; it cannot be moved.`;
            status.title = 'Can\'t reschedule this message';
            status.primaryFunction = null;
          } else {
            status.primaryCopy = `This message sends once for everyone in the ${startDate} cohort, so rescheduling will update for everyone.`;
            status.primaryFunctionDisabled = false;
          }
        } else {
          status.primaryFunctionDisabled = false;
          status.primaryCopy = `Reschedule this message for everyone in the ${startDate} cohort?`;
          status.secondaryFunction = () => { apply(false); };
          status.secondaryFunctionText = `Apply only to ${state.onboardingSessions.session.user}`;
          const secondaryTexts = [];
          if (occurred) secondaryTexts.push(`This message already triggered for ${occurredCopy}, so this won't be rescheduled for them.`);
          if (movingToPast) secondaryTexts.push(`This time is in the past for ${movingToPastCopy}, so this won't be rescheduled for them.`);
          if (secondaryTexts.length) status.secondaryCopy = secondaryTexts.join(' ');
        }
      } else {
        // Error loading batchCounterparts. Don't let them apply to cohort, we don't have enough information.
        status.primaryCopy = 'Could not load information about other people in this cohort.';
      }
    }
    return status;
  });
  const fetchBatchCounterparts = () => {
    const { eventId, day } = state.modal.data;
    Network.get(
      `/${state.onboardingSessions.flowType.routeResource}/session_events/${eventId}/reschedule_attempt_data?day=${day}`,
      {
        success: (data) => { batchCounterparts.value = data; },
        error: () => { batchCounterparts.value = null; },
        complete: () => { fetchingBatchCounterparts.value = false; },
      },
    );
  };

  // Lifcycle hooks
  onMounted(() => {
    fetchBatchCounterparts();
  });
</script>
